import {
    ModalOverlay,
    ModalDialog,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    LoadingDots
} from '@flos/react-ui';
import { useHippoText } from '../../../hooks/useHippoText';
import getAdjustCoverageConfig from '../config/AdjustCoverageConfig';

import type { HandleCloseOPCModal } from '../types';
import { useAdjustCoverageOPC } from '../../../hooks/useAdjustCoverageOPC';
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { jobAtom } from '../../../recoil/atoms/jobAtom';
import useOpcJob from '../../../hooks/useOpcJob';
import usePageNavigation from '../../../hooks/usePageNavigation';
import AttributeChangeInformation from './EditCoverableAttribute/AttributeChangeInformation';
import { callMeVariantOpcAtom } from '../../../recoil/atoms/callMeVariantOpcAtom';
import {
    ACC_DEATH_COVERAGE_TERM,
    ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
    ACC_INJURY_TOOTH_DAMAGE_EIGHT_PERCENT,
    ACC_INJURY_TOOTH_DAMAGE_FIVE_PERCENT,
    ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
    CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
} from '../../../constants/term';
import { isOpcDataFilledAtom } from '../../../recoil/atoms/isOpcDataFilledAtom';
import { isOpcFetchingAtom } from '../../../recoil/atoms/isOpcFetchingAtom';
import { track } from '../../../utils/trackingUtil';
import { opcInfoAtom } from '../../../recoil/atoms/opcInfoAtom';
import { refreshCoverableChangesSelectorAtom } from '../../../recoil/atoms/refreshCoverableChangesSelectorAtom';
import { opcCoverableStatusSelector } from '../../../recoil/selectors/opcCoverableStatusSelector';
import { useQuoteJob } from '../../../hooks/useQuoteJob';
import { useAwaitQuoteJob } from '../../../hooks/useAwaitQuotedJob';
import { useIssueInsurance } from '../../../hooks/useIssueInsurance';
import { useAwaitBoundJob } from '../../../hooks/useAwaitBoundJob';
import { useRef, useState } from 'react';
import LoadingModal from '../../../shared_components/LoadingModal/LoadingModal';
import CustomerStopModal from '../../../shared_components/CustomerStopModal/CustomerStopModal';
import type { CustomerStopCause } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { getEffectiveDateRuleInfoSelector } from '../../../recoil/selectors/getEffectiveDateRuleSelector';
import { convertLocalDateDTOToDate, convertLocalDateToString } from '../../../utils/dateUtil';
import { allCoverablesAtom } from '../../../recoil/atoms/allCoverablesAtom';
import { refreshCoverablesSelectorAtom } from '../../../recoil/atoms/refreshCoverablesSelectorAtom';
import { getOPCReceiptPageChangeTypeAtom } from '../../../recoil/atoms/getOPCReceiptPageChangeTypeAtom';
import { opcOldPriceAtom } from '../../../recoil/atoms/opcOldPriceAtom';
import InterdependentChangeInfo from './InterdependentChangeInfo';

const AdjustCoverageModal = ({
    handleCloseModal,
    shouldNavigateToShoppingBasket
}: {
    handleCloseModal: HandleCloseOPCModal;
    shouldNavigateToShoppingBasket: boolean;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const isFullScreen = false;
    const {
        termInfo,
        editTermPriceDTO,
        updateTermValue,
        updateTermStringValue,
        adjustAction,
        isAdjustableDataValid,
        validateAdjustableDeductible,
        revertChange
    } = useAdjustCoverageOPC();
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);
    const isOpcDataFilled = useRecoilValue(isOpcDataFilledAtom);
    const { isRuleActive, effectiveDate } = useRecoilValue(getEffectiveDateRuleInfoSelector);
    const { selectedPaymentFrequency } = useRecoilValue(jobAtom);
    const { isFromDAFlow } = useRecoilValue(opcInfoAtom);
    const { createNewPolicyChange, withdrawCurrentOPCShoppingBasketIfNotExisting } = useOpcJob();
    const { goToShoppingBasket, goToReceiptPage } = usePageNavigation();
    const setCallMeVariant = useSetRecoilState(callMeVariantOpcAtom);
    const setRefreshCoverableChangesSelector = useSetRecoilState(
        refreshCoverableChangesSelectorAtom
    );
    const { isAddedCoverable } = useRecoilValue(opcCoverableStatusSelector);
    const shouldHideDateText = isFromDAFlow && isAddedCoverable;
    const maximumValue = termInfo?.valueMax ?? 0;

    const { quoteJob } = useQuoteJob();
    const { awaitQuoteJob } = useAwaitQuoteJob();
    const { issueInsurance } = useIssueInsurance();
    const { awaitBoundJob } = useAwaitBoundJob(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [stopCause, setStopCause] = useState<CustomerStopCause>('None');
    const setJobAtom = useSetRecoilState(jobAtom);
    const [isOpen, setIsOpen] = useState(true);
    const hasTriggeredIssuing = useRef(false);
    const opcOriginalPrice = useRecoilValue(opcOldPriceAtom);

    const resetCoverables = useResetRecoilState(allCoverablesAtom);
    const setRefreshCoverablesSelector = useSetRecoilState(refreshCoverablesSelectorAtom);

    const setOPCReceiptPageChangeType = useSetRecoilState(getOPCReceiptPageChangeTypeAtom);

    if (!adjustAction) throw new Error('Unknown action');

    if (!termInfo || !editTermPriceDTO) return <LoadingDots />;

    const { title, AdjustComponent, adjustInformationProps } = getAdjustCoverageConfig(
        adjustAction,
        editTermPriceDTO,
        termInfo.directValue ?? 0,
        termInfo.value ?? '0',
        opcOriginalPrice,
        selectedPaymentFrequency
    );

    const hasNoChanges =
        adjustInformationProps?.mobileProps.before.attribute.value !== undefined &&
        adjustInformationProps?.mobileProps.after.attribute.value !== undefined &&
        adjustInformationProps?.mobileProps.before.attribute.value ===
            adjustInformationProps?.mobileProps.after.attribute.value &&
        adjustInformationProps?.mobileProps.before.price.value ===
            adjustInformationProps?.mobileProps.after.price.value;

    const isShowingOldPriceOnly =
        adjustInformationProps?.mobileProps.before.attribute.value !== undefined &&
        adjustInformationProps?.mobileProps.before.price.value !== undefined &&
        adjustInformationProps?.mobileProps.after.attribute.value === '-' &&
        adjustInformationProps?.mobileProps.after.price.value === '-';

    const hasIncreaseInjuryDegree =
        termInfo.publicId === ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM &&
        termInfo.value === String(ACC_INJURY_TOOTH_DAMAGE_FIVE_PERCENT) &&
        editTermPriceDTO.newTermValue?.amount === ACC_INJURY_TOOTH_DAMAGE_EIGHT_PERCENT;

    const exceededDeathCoverageLimit =
        termInfo.publicId === ACC_DEATH_COVERAGE_TERM &&
        (editTermPriceDTO.newTermValue?.amount ?? 0) > maximumValue;

    const exceededPermanentInjuryInsuredSum =
        termInfo.publicId === ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM &&
        (editTermPriceDTO.newTermValue?.amount ?? 0) > maximumValue;

    const exceededChildPermanentInjuryInsuredSum =
        termInfo.publicId === CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM &&
        (editTermPriceDTO.newTermValue?.amount ?? 0) > maximumValue;

    const lowerAccInsuranceSumThanPrevious =
        adjustAction === 'Limit' &&
        (termInfo.publicId === ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM ||
            termInfo.publicId === CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM) &&
        (editTermPriceDTO.newTermValue?.amount ?? 0) < Number(termInfo.value);

    if (!adjustInformationProps) return <LoadingDots />;

    const trackChangedCoverage = (): void => {
        if (!editTermPriceDTO.coverageName || !editTermPriceDTO.coverableName) return;
        const trackingKey = adjustAction === 'Limit' ? 'InsuranceSum' : adjustAction;

        track(
            `OPCEditCoverage-${editTermPriceDTO.coverableName}-${editTermPriceDTO.coverageName}`,
            [
                { newYearlyPrice: editTermPriceDTO.newPrice?.yearlyTotal.amount },
                { newMonthlyPrice: editTermPriceDTO.newPrice?.monthlyTotal.amount },
                { [trackingKey]: editTermPriceDTO.newTermValue?.amount }
            ]
        );
    };

    const handleClearChange = async (): Promise<void> => {
        await withdrawCurrentOPCShoppingBasketIfNotExisting();
        await createNewPolicyChange();
    };

    const handleRevertChange = async (): Promise<void> => {
        await revertChange();
        shouldNavigateToShoppingBasket && goToShoppingBasket(false);
    };

    const closeCallback = isFromDAFlow ? handleRevertChange : handleClearChange;

    const quoteAndIssueJobInBackground = async (): Promise<void> => {
        if (hasTriggeredIssuing.current) return;
        hasTriggeredIssuing.current = true;
        const response = await quoteJob();
        if (response?.stopCause !== 'None') {
            setStopCause(response?.stopCause);
            setIsOpen(false);
            return;
        } else {
            await awaitQuoteJob();
        }

        await issueInsurance();
        const { status } = await awaitBoundJob();
        setJobAtom((jobDTO) => ({ ...jobDTO, jobStatus: status }));
    };

    const onFinished = (): void => {
        setShowLoadingModal(false);
        if (stopCause === 'None') {
            handleCloseModal(goToReceiptPage, 'goToReceiptPage');
            resetCoverables();
            setRefreshCoverablesSelector({});
            setOPCReceiptPageChangeType('adjustCoverage');
        }
    };

    return (
        <ModalOverlay isOpen={isOpen} isFullScreen={isFullScreen}>
            <ModalDialog
                isFullScreen={isFullScreen}
                closeIconHandler={() => {
                    if (isOpcFetching) return;
                    handleCloseModal(closeCallback);
                }}
            >
                <ModalHeader
                    title={`${getHippoText(title)}: ${editTermPriceDTO.coverageName ?? ''}`}
                />
                <ModalBody>
                    <AdjustComponent
                        termInfo={termInfo}
                        updateTermValue={updateTermValue}
                        updateTermStringValue={updateTermStringValue}
                        isAdjustableDataValid={isAdjustableDataValid}
                    />
                    {isOpcFetching ? (
                        <LoadingDots />
                    ) : (
                        <AttributeChangeInformation attributeChangeProps={adjustInformationProps} />
                    )}
                    <InterdependentChangeInfo
                        termId={termInfo.publicId}
                        newTermValue={editTermPriceDTO.newTermValue?.amount}
                    />
                    {isRuleActive && !shouldHideDateText && (
                        <h6>
                            {getHippoText('DSS-EFFECTIVE-DATE-RULE-MOTOR-TEXT', {
                                date:
                                    convertLocalDateToString(
                                        convertLocalDateDTOToDate(effectiveDate ?? {})
                                    ) ?? ''
                            })}
                        </h6>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        disabled={
                            isOpcFetching ||
                            !isOpcDataFilled ||
                            hasNoChanges ||
                            isShowingOldPriceOnly
                        }
                        onClick={() => {
                            if (adjustAction === 'Deductible') {
                                const canGoNext = validateAdjustableDeductible();
                                // TODO: Use hippo label when label id is define in document [Hippo ACC]
                                if (canGoNext) {
                                    trackChangedCoverage();
                                    setRefreshCoverableChangesSelector({});
                                    if (isFromDAFlow) {
                                        handleCloseModal(goToShoppingBasket, 'goToShoppingBasket');
                                    } else {
                                        setIsOpen(false);
                                        setShowLoadingModal(true);
                                    }
                                }
                                return;
                            }
                            if (hasIncreaseInjuryDegree) {
                                setCallMeVariant('injuryDegree');
                                return;
                            }
                            if (
                                exceededDeathCoverageLimit ||
                                exceededPermanentInjuryInsuredSum ||
                                exceededChildPermanentInjuryInsuredSum
                            ) {
                                setCallMeVariant('deathCovExceedLimit');
                                return;
                            }

                            if (!isFromDAFlow && lowerAccInsuranceSumThanPrevious) {
                                setCallMeVariant('lowerAccInsuranceSumThanPrevious');
                                return;
                            }

                            trackChangedCoverage();
                            if (isFromDAFlow) {
                                handleCloseModal(goToShoppingBasket, 'goToShoppingBasket');
                            } else {
                                setIsOpen(false);
                                setShowLoadingModal(true);
                            }
                        }}
                    >
                        {getHippoText('DSS-COVERABLE-DETAILS-ACCEPT')}
                    </Button>
                    <Button
                        theme="secondary"
                        onClick={() => {
                            handleCloseModal(closeCallback);
                        }}
                        disabled={isOpcFetching}
                    >
                        {getHippoText('DSS-COVERABLE-DETAILS-CANCEL')}
                    </Button>
                </ModalFooter>
            </ModalDialog>
            {showLoadingModal && (
                <LoadingModal
                    loadingTitle={getHippoText('DSS-LOADING-HEADER')}
                    loadingText={getHippoText('DSS-LOADING-TEXT')}
                    showModal={showLoadingModal}
                    closeModal={() => {
                        setShowLoadingModal(false);
                    }}
                    backgroundTask={quoteAndIssueJobInBackground}
                    onFinished={onFinished}
                />
            )}
            {stopCause !== 'None' && (
                <CustomerStopModal
                    showModal={true}
                    stopCause={stopCause}
                    closeModal={() => {
                        setIsOpen(true);
                        setStopCause('None');
                    }}
                />
            )}
        </ModalOverlay>
    );
};

export default AdjustCoverageModal;
